import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { api } from "../api/client";
import { Modal } from "./Modal";
import { toast } from "./Toaster";

interface MoveListCardsDialogProps {
  boardId: string;
  /** The list being emptied. */
  listId: string;
  listTitle: string;
  /** How many cards sit on the list's face right now. */
  cardCount: number;
  /** Candidate targets — every other list on this board, in board order. */
  targets: { id: string; title: string }[];
  onClose: () => void;
}

/**
 * Move every card in a column to another list on the same board. Cross-board
 * moves stay a per-card operation (they drop labels and assignees, which is
 * not something to do to a whole column behind one click).
 */
export function MoveListCardsDialog({
  boardId,
  listId,
  listTitle,
  cardCount,
  targets,
  onClose,
}: MoveListCardsDialogProps) {
  const qc = useQueryClient();
  const [targetId, setTargetId] = useState(targets[0]?.id ?? "");

  const move = useMutation({
    mutationFn: () => api.lists.moveCards(listId, { listId: targetId }),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ["board", boardId] });
      const target = targets.find((l) => l.id === targetId);
      toast(
        `Moved ${result.count} ${result.count === 1 ? "card" : "cards"} to ${
          target?.title ?? "the target list"
        }`,
      );
      onClose();
    },
    onError: (e: Error) => toast(e.message),
  });

  return (
    <Modal onClose={onClose} ariaLabel="Move all cards">
      <div className="p-5 sm:p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Move all cards</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <X size={16} />
          </button>
        </div>

        {targets.length === 0 ? (
          <p className="text-sm text-slate-500">
            There's nowhere to move them — this board has no other list.
          </p>
        ) : (
          <>
            <p className="mb-3 text-sm text-slate-600">
              Move all {cardCount} {cardCount === 1 ? "card" : "cards"} out of{" "}
              <span className="font-medium text-slate-800">{listTitle}</span>,
              keeping their order. They're appended to the end of:
            </p>
            <label className="block text-sm">
              <span className="text-slate-600">List</span>
              <select
                value={targetId}
                onChange={(e) => setTargetId(e.target.value)}
                className="mt-1 block w-full rounded border border-slate-300 bg-[var(--surface)] px-2 py-2 text-sm"
              >
                {targets.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.title}
                  </option>
                ))}
              </select>
            </label>
          </>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => move.mutate()}
            disabled={!targetId || move.isPending}
            className="rounded bg-blue-600 px-4 py-1.5 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {move.isPending ? "Moving…" : "Move cards"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
